import {
  DpeState,
  PropertyApartmentSubType,
  PropertyApplicationService,
  PropertyApplicationStatus,
  PropertyCharacteristicRefDto,
  PropertyCharacteristicsDto,
  PropertyCommercialSubType,
  PropertyCommonAreaType,
  PropertyCondition,
  PropertyConstructionMaterial,
  PropertyConstructionPeriod,
  PropertyCoOwnershipProcedureType,
  PropertyCreationSource,
  PropertyCurrentMarketingMode,
  PropertyDocumentStatus,
  PropertyDocumentType,
  PropertyExposure,
  PropertyGeolocationType,
  PropertyHeatingEnergy,
  PropertyHeatingType,
  PropertyHouseSubType,
  PropertyKeyManagement,
  PropertyLandSubType,
  PropertyMandateDuration,
  PropertyMandateType,
  PropertyMarketState,
  PropertyOfferType,
  PropertyOrientation,
  PropertyParkingSubType,
  PropertyProfessionalSubType,
  PropertyResidenceType,
  PropertyRoofMaterial,
  PropertyRoomType,
  PropertySaleUrgency,
  PropertySellerPriority,
  PropertySellerRole,
  PropertyStanding,
  PropertySubtype,
  PropertyType,
  PropertyVerificationStatus,
  PropertyView,
} from '@workspace/data-types';

export class EtxeEnumsNiceLabelUtils {
  static offerType(type: PropertyOfferType): string {
    switch (type) {
      case PropertyOfferType.SALE:
        return 'Vente';
      case PropertyOfferType.RENTAL:
        return 'Location';
      case PropertyOfferType.LIFE_ANNUITY:
        return 'Viager';
      case PropertyOfferType.FORWARD_SALE:
        return 'Vente à terme';
      default:
        return type;
    }
  }

  static residenceType(type?: PropertyResidenceType | null): string {
    switch (type) {
      case PropertyResidenceType.MAIN:
        return 'Résidence principale';
      case PropertyResidenceType.SECONDARY:
        return 'Résidence secondaire';
      case PropertyResidenceType.RENTAL_INVESTMENT:
        return 'Investissement locatif';
      default:
        return type || '';
    }
  }

  static sellerRole(role: PropertySellerRole): string {
    switch (role) {
      case PropertySellerRole.OWNER:
        return 'Propriétaire en direct';
      case PropertySellerRole.FAMILY:
        return 'J’aide un proche / Mandataire familial';
      case PropertySellerRole.ESTATE_HEIR:
        return 'Succession / Indivision';
      case PropertySellerRole.COMPANY:
        return 'Société / SCI';
      default:
        return role;
    }
  }

  static propertyType(type: PropertyType): string {
    switch (type) {
      case PropertyType.HOUSE:
        return 'Maison';
      case PropertyType.APARTMENT:
        return 'Appartement';
      case PropertyType.LAND:
        return 'Terrain';
      case PropertyType.PARKING_BOX:
        return 'Stationnement';
      case PropertyType.BUSINESS:
        return 'Fonds de commerce';
      case PropertyType.PROFESSIONAL:
        return 'Local professionnel';
      default:
        return 'Bien';
    }
  }

  static propertySubtype(subtype?: PropertySubtype | null, propertyType?: PropertyType | null): string {
    if (!subtype) return '';
    if (propertyType) {
      switch (propertyType) {
        case PropertyType.HOUSE:
          return EtxeEnumsNiceLabelUtils.house(subtype as PropertyHouseSubType);
        case PropertyType.APARTMENT:
          return EtxeEnumsNiceLabelUtils.apartment(subtype as PropertyApartmentSubType);
        case PropertyType.BUSINESS:
          return EtxeEnumsNiceLabelUtils.commercial(subtype as PropertyCommercialSubType);
        case PropertyType.LAND:
          return EtxeEnumsNiceLabelUtils.land(subtype as PropertyLandSubType);
        case PropertyType.PARKING_BOX:
          return EtxeEnumsNiceLabelUtils.parking(subtype as PropertyParkingSubType);
        case PropertyType.PROFESSIONAL:
          return EtxeEnumsNiceLabelUtils.professional(subtype as PropertyProfessionalSubType);
      }
    }
    if (Object.values(PropertyHouseSubType).includes(subtype as PropertyHouseSubType)) {
      return EtxeEnumsNiceLabelUtils.house(subtype as PropertyHouseSubType);
    }
    if (Object.values(PropertyApartmentSubType).includes(subtype as PropertyApartmentSubType)) {
      return EtxeEnumsNiceLabelUtils.apartment(subtype as PropertyApartmentSubType);
    }
    if (Object.values(PropertyCommercialSubType).includes(subtype as PropertyCommercialSubType)) {
      return EtxeEnumsNiceLabelUtils.commercial(subtype as PropertyCommercialSubType);
    }
    if (Object.values(PropertyLandSubType).includes(subtype as PropertyLandSubType)) {
      return EtxeEnumsNiceLabelUtils.land(subtype as PropertyLandSubType);
    }
    if (Object.values(PropertyParkingSubType).includes(subtype as PropertyParkingSubType)) {
      return EtxeEnumsNiceLabelUtils.parking(subtype as PropertyParkingSubType);
    }
    if (Object.values(PropertyProfessionalSubType).includes(subtype as PropertyProfessionalSubType)) {
      return EtxeEnumsNiceLabelUtils.professional(subtype as PropertyProfessionalSubType);
    }
    return String(subtype);
  }

  static floorConfig(numberOfFloors?: number | null, propertyType?: PropertyType | null): string {
    const isHouse = propertyType === PropertyType.HOUSE;
    const isApartment = propertyType === PropertyType.APARTMENT;

    if (isHouse) {
      if (numberOfFloors === 0 || numberOfFloors === undefined || numberOfFloors === null) {
        return 'Plain-pied';
      }
      if (numberOfFloors === 1) return '1 étage';
      return `${numberOfFloors} étages`;
    }

    if (isApartment || numberOfFloors !== undefined) {
      if (numberOfFloors === 0 || numberOfFloors === undefined || numberOfFloors === null) {
        return 'Sans étage';
      }
      if (numberOfFloors === 1) return '1 étage (2 niveaux)';
      return `${numberOfFloors} étages (${numberOfFloors + 1} niveaux)`;
    }

    return '';
  }

  static apartmentFloor(floor?: number | null, buildingTotal?: number | null): string {
    const hasFloor = floor !== undefined && floor !== null;
    const hasTotal = buildingTotal !== undefined && buildingTotal !== null;

    if (!hasFloor && !hasTotal) return '';

    let floorStr = '-';
    if (hasFloor) {
      floorStr = floor === 0 ? 'RDC' : `${floor}${floor === 1 ? 'er' : 'e'}`;
    }

    if (hasTotal) {
      return `${floorStr} / ${buildingTotal} étage${buildingTotal > 1 ? 's' : ''}`;
    }
    return floorStr;
  }

  static remainingDays(date: Date | string | undefined | null): string {
    if (!date) return '';
    const target = new Date(date);
    target.setHours(0, 0, 0, 0);
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    const diffDays = Math.round((target.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

    if (diffDays > 1) {
      return `dans ${diffDays} jours`;
    }
    if (diffDays === 1) {
      return 'dans 1 jour';
    }
    if (diffDays === 0) {
      return "aujourd'hui";
    }
    const pastDays = Math.abs(diffDays);
    return `expiré depuis ${pastDays} jour${pastDays > 1 ? 's' : ''}`;
  }

  static geolocationType(type?: PropertyGeolocationType | string | null): string {
    switch (type) {
      case PropertyGeolocationType.NEIGHBORHOOD:
        return 'Quartier';
      case PropertyGeolocationType.CITY:
        return 'Ville';
      case PropertyGeolocationType.REGION:
        return 'Région';
      default:
        return type || 'Quartier';
    }
  }

  static land(land: PropertyLandSubType): string {
    switch (land) {
      case PropertyLandSubType.BUILDING:
        return 'Constructible';
      case PropertyLandSubType.AGRICULTURAL:
        return 'Agricole';
      case PropertyLandSubType.LEISURE:
        return 'Loisir';
      case PropertyLandSubType.NON_BUILDING:
        return 'Non constructible';
      default:
        return land;
    }
  }

  static house(house: PropertyHouseSubType): string {
    switch (house) {
      case PropertyHouseSubType.TERRACED:
        return 'Maison mitoyenne';
      case PropertyHouseSubType.VILLA:
        return 'Villa';
      case PropertyHouseSubType.FARM:
        return 'Ferme';
      case PropertyHouseSubType.MANOR:
        return 'Manoir';
      case PropertyHouseSubType.CASTLE:
        return 'Château';
      case PropertyHouseSubType.BARN:
        return 'Grange';
      case PropertyHouseSubType.PROPERTY:
        return 'Propriété';
      case PropertyHouseSubType.OTHER:
        return 'Autre';
      default:
        return house;
    }
  }

  static apartment(apartment: PropertyApartmentSubType): string {
    switch (apartment) {
      case PropertyApartmentSubType.SIMPLEX:
        return 'Appartement de plain-pied';
      case PropertyApartmentSubType.DUPLEX:
        return 'Duplex';
      case PropertyApartmentSubType.TRIPLEX:
        return 'Triplex';
      case PropertyApartmentSubType.SOUFLEX:
        return 'Souplex';
      case PropertyApartmentSubType.LOFT:
        return 'Loft ou atelier';
      default:
        return apartment;
    }
  }

  static apartmentDescription(apartment: PropertyApartmentSubType): string {
    switch (apartment) {
      case PropertyApartmentSubType.SIMPLEX:
        return 'Aménagé sur un seul et même niveau';
      case PropertyApartmentSubType.DUPLEX:
        return 'Aménagé sur deux niveaux avec escalier intérieur';
      case PropertyApartmentSubType.TRIPLEX:
        return 'Aménagé sur trois niveaux avec escalier intérieur';
      case PropertyApartmentSubType.SOUFLEX:
        return 'Aménagé sur deux niveaux dont un en sous-sol';
      case PropertyApartmentSubType.LOFT:
        return 'Grand volume ouvert ou réhabilitation atypique';
      default:
        return '';
    }
  }

  static commercial(commercial: PropertyCommercialSubType): string {
    switch (commercial) {
      case PropertyCommercialSubType.HOTEL:
        return 'Hôtel';
      case PropertyCommercialSubType.SHOP:
        return 'Boutique';
      case PropertyCommercialSubType.PRESS:
        return 'Presse';
      case PropertyCommercialSubType.RESTAURANT:
        return 'Restaurant';
      case PropertyCommercialSubType.BAR:
        return 'Bar';
      default:
        return commercial;
    }
  }

  static professional(pro: PropertyProfessionalSubType): string {
    switch (pro) {
      case PropertyProfessionalSubType.SHOP:
        return 'Boutique';
      case PropertyProfessionalSubType.OFFICE:
        return 'Bureau';
      case PropertyProfessionalSubType.WAREHOUSE:
        return 'Entrepôt';
      case PropertyProfessionalSubType.COMMERCIAL_PREMISES:
        return 'Local commercial';
      case PropertyProfessionalSubType.INDUSTRIAL:
        return 'Industriel';
      default:
        return pro;
    }
  }

  static room(room: PropertyRoomType): string {
    switch (room) {
      case PropertyRoomType.LIVING_ROOM:
        return 'Salon / Séjour';
      case PropertyRoomType.DINING_ROOM:
        return 'Salle à manger';
      case PropertyRoomType.KITCHEN:
        return 'Cuisine';
      case PropertyRoomType.OFFICE:
        return 'Bureau';
      case PropertyRoomType.LIBRARY:
        return 'Bibliothèque';
      case PropertyRoomType.WORKSHOP:
        return 'Atelier';
      case PropertyRoomType.MUSIC_STUDIO:
        return 'Studio de musique';
      case PropertyRoomType.LAUNDRY_ROOM:
        return 'Buanderie';
      case PropertyRoomType.GYM:
        return 'Salle de sport';
      case PropertyRoomType.PLAYROOM:
        return 'Salle de jeux';
      case PropertyRoomType.STORAGE:
        return 'Débarras';
      case PropertyRoomType.BASEMENT:
        return 'Sous-sol'; // Changed from 'Cave' to match previous 'Sous-sol' label if preferred, or keep explicit map
      case PropertyRoomType.ATTIC:
        return 'Grenier';
      case PropertyRoomType.OTHER:
        return 'Autre';
      default:
        return room;
    }
  }

  static heatingType(type: PropertyHeatingType | string): string {
    switch (type) {
      case PropertyHeatingType.INDIVIDUAL:
        return 'Individuel';
      case PropertyHeatingType.COLLECTIVE:
        return 'Collectif';
      default:
        return type || '';
    }
  }

  static heatingEnergy(energy: PropertyHeatingEnergy): string {
    switch (energy) {
      case PropertyHeatingEnergy.ELECTRICITY:
        return 'Électricité';
      case PropertyHeatingEnergy.GAS:
        return 'Gaz';
      case PropertyHeatingEnergy.OIL:
        return 'Fioul';
      case PropertyHeatingEnergy.WOOD:
        return 'Bois';
      case PropertyHeatingEnergy.SOLAR:
        return 'Solaire';
      case PropertyHeatingEnergy.GEOTHERMAL:
        return 'Géothermie';
      case PropertyHeatingEnergy.AEROTHERMAL:
        return 'Aérothermie';
      case PropertyHeatingEnergy.HEAT_PUMP:
        return 'Pompe à chaleur';
      case PropertyHeatingEnergy.UNDERFLOOR:
        return 'Au sol';
      case PropertyHeatingEnergy.AIR_CONDITIONING:
        return 'Climatisation';
      case PropertyHeatingEnergy.PELLETS:
        return 'Granulé';
      case PropertyHeatingEnergy.NONE:
        return 'Aucun';
      case PropertyHeatingEnergy.OTHER:
        return 'Autre';
      default:
        return energy;
    }
  }

  static condition(condition: PropertyCondition): string {
    switch (condition) {
      case PropertyCondition.AS_NEW:
        return 'Neuf / récemment rénové';
      case PropertyCondition.GOOD:
        return 'Bon état';
      case PropertyCondition.PARTIALLY_RENOVATED:
        return 'Rafraîchissements nécessaires';
      case PropertyCondition.RENOVATION_NEEDED:
        return 'À rénover / Travaux importants';
      case PropertyCondition.TO_BE_DEMOLISHED:
        return 'Destiné à être démoli';
      default:
        return condition;
    }
  }

  static constructionMaterial(material: PropertyConstructionMaterial): string {
    switch (material) {
      case PropertyConstructionMaterial.STONE:
        return 'Pierre';
      case PropertyConstructionMaterial.BRICK:
        return 'Brique';
      case PropertyConstructionMaterial.CONCRETE:
        return 'Béton';
      case PropertyConstructionMaterial.WOOD:
        return 'Bois';
      case PropertyConstructionMaterial.CINDERBLOCK:
        return 'Parpaing';
      case PropertyConstructionMaterial.RAMMED_EARTH:
        return 'Pisé / Terre';
      case PropertyConstructionMaterial.METAL:
        return 'Métal';
      case PropertyConstructionMaterial.OTHER:
        return 'Autre';
      default:
        return material;
    }
  }

  static roofMaterial(material: PropertyRoofMaterial): string {
    switch (material) {
      case PropertyRoofMaterial.TILES:
        return 'Tuiles';
      case PropertyRoofMaterial.SLATE:
        return 'Ardoises';
      case PropertyRoofMaterial.ZINC:
        return 'Zinc';
      case PropertyRoofMaterial.THATCH:
        return 'Chaume';
      case PropertyRoofMaterial.CONCRETE:
        return 'Béton / Terrasse';
      case PropertyRoofMaterial.SHINGLE:
        return 'Bardeau bitumé';
      case PropertyRoofMaterial.METAL:
        return 'Bac acier / Métal';
      case PropertyRoofMaterial.OTHER:
        return 'Autre';
      default:
        return material;
    }
  }

  static orientation(orientation: PropertyOrientation): string {
    switch (orientation) {
      case PropertyOrientation.NORTH:
        return 'Nord';
      case PropertyOrientation.NORTH_EAST:
        return 'Nord-Est';
      case PropertyOrientation.NORTH_WEST:
        return 'Nord-Ouest';
      case PropertyOrientation.SOUTH:
        return 'Sud';
      case PropertyOrientation.SOUTH_EAST:
        return 'Sud-Est';
      case PropertyOrientation.SOUTH_WEST:
        return 'Sud-Ouest';
      case PropertyOrientation.EAST:
        return 'Est';
      case PropertyOrientation.WEST:
        return 'Ouest';
      default:
        return orientation;
    }
  }

  static parking(parking: PropertyParkingSubType): string {
    switch (parking) {
      case PropertyParkingSubType.BOX:
        return 'Box';
      case PropertyParkingSubType.CARPORT:
        return 'Carport';
      case PropertyParkingSubType.GARAGE:
        return 'Garage';
      case PropertyParkingSubType.DOUBLE_GARAGE:
        return 'Garage double';
      case PropertyParkingSubType.COVERED:
        return 'Parking couvert';
      case PropertyParkingSubType.UNDERGROUND:
        return 'Souterrain';
      case PropertyParkingSubType.OUTDOOR:
        return 'Place extérieure';
      case PropertyParkingSubType.STREET_PARKING:
        return 'Stationnement en voirie';
      default:
        return parking;
    }
  }

  static commonArea(area: PropertyCommonAreaType): string {
    switch (area) {
      case PropertyCommonAreaType.HALL:
        return 'Hall';
      case PropertyCommonAreaType.SWIMMING_POOL:
        return 'Piscine';
      case PropertyCommonAreaType.GARDEN:
        return 'Jardin';
      case PropertyCommonAreaType.COURTYARD:
        return 'Cour';
      case PropertyCommonAreaType.PARK:
        return 'Parc';
      case PropertyCommonAreaType.BIKE_ROOM:
        return 'Local vélo';
      case PropertyCommonAreaType.STROLLER_ROOM:
        return 'Local poussette';
      case PropertyCommonAreaType.TRASH_ROOM:
        return 'Local poubelle';
      case PropertyCommonAreaType.MEETING_ROOM:
        return 'Salle de réunion';
      case PropertyCommonAreaType.GYM:
        return 'Salle de sport';
      case PropertyCommonAreaType.OTHER:
        return 'Autre';
      default:
        return area;
    }
  }

  static dpeState(state: DpeState): string {
    switch (state) {
      case DpeState.DONE:
        return 'Réalisé';
      case DpeState.IN_PROGRESS:
        return 'En cours';
      case DpeState.TO_DO:
        return 'À réaliser';
      case DpeState.NOT_SUBJECT:
        return 'Non soumis';
      default:
        return state;
    }
  }

  static coOwnershipProcedure(type: PropertyCoOwnershipProcedureType): string {
    switch (type) {
      case PropertyCoOwnershipProcedureType.ALERT:
        return 'Procédure d’alerte';
      case PropertyCoOwnershipProcedureType.JUDICIAL_REORGANIZATION:
        return 'Procédure de redressement judiciaire';
      case PropertyCoOwnershipProcedureType.DEFICIENCY_STATE:
        return 'Procédure pour copropriété en état de carence';
      case PropertyCoOwnershipProcedureType.OTHER:
        return 'Autre';
      default:
        return type;
    }
  }

  static exposure(exposure: PropertyExposure): string {
    switch (exposure) {
      case PropertyExposure.FAINT:
        return 'Faible (peu ou pas de soleil direct)';
      case PropertyExposure.MODERATE:
        return 'Modérée (soleil une partie de la journée)';
      case PropertyExposure.GOOD:
        return 'Bonne (ensoleillé plusieurs heures par jour)';
      case PropertyExposure.EXCELLENT:
        return 'Très bonne (ensoleillé toute la journée)';
      default:
        return exposure;
    }
  }

  static constructionPeriod(value: PropertyConstructionPeriod): string {
    switch (value) {
      case PropertyConstructionPeriod.BEFORE_1850:
        return 'Avant 1850';
      case PropertyConstructionPeriod.PERIOD_1850_1913:
        return '1850-1913';
      case PropertyConstructionPeriod.PERIOD_1914_1947:
        return '1914-1947';
      case PropertyConstructionPeriod.PERIOD_1948_1969:
        return '1948-1969';
      case PropertyConstructionPeriod.PERIOD_1970_1980:
        return '1970-1980';
      case PropertyConstructionPeriod.PERIOD_1981_1991:
        return '1981-1991';
      case PropertyConstructionPeriod.PERIOD_1992_2000:
        return '1992-2000';
      case PropertyConstructionPeriod.PERIOD_2001_2010:
        return '2001-2010';
      case PropertyConstructionPeriod.PERIOD_2011_2018:
        return '2011-2018';
      case PropertyConstructionPeriod.SINCE_2019:
        return 'Depuis 2019';
      default:
        return value;
    }
  }

  static propertyStanding(value?: PropertyStanding): string {
    if (!value) return '-';
    switch (value) {
      case PropertyStanding.LUXURY:
        return 'Luxe';
      case PropertyStanding.PREMIUM:
        return 'Haut de gamme';
      case PropertyStanding.MID_RANGE:
        return 'Milieu de gamme';
      case PropertyStanding.ENTRY_LEVEL:
        return 'Entrée de gamme';
      default:
        return value;
    }
  }

  static propertyView(value?: PropertyView): string {
    if (!value) return '-';
    switch (value) {
      case PropertyView.SEA:
        return 'Mer';
      case PropertyView.MOUNTAIN:
        return 'Montagne';
      case PropertyView.MONUMENT:
        return 'Monument historique';
      case PropertyView.UNOBSTRUCTED:
        return 'Dégagée';
      case PropertyView.GARDEN:
        return 'Jardin / Parc';
      case PropertyView.CITY:
        return 'Ville';
      case PropertyView.STREET:
        return 'Rue';
      case PropertyView.QUIET:
        return 'Calme';
      case PropertyView.OPPOSITE:
        return 'Vis-à-vis';
      default:
        return value;
    }
  }

  static saleUrgency(value?: PropertySaleUrgency): string {
    if (!value) return '-';
    switch (value) {
      case PropertySaleUrgency.AS_SOON_AS_POSSIBLE:
        return 'Au plus vite';
      case PropertySaleUrgency.ONE_TO_THREE_MONTHS:
        return 'Dans 1 à 3 mois';
      case PropertySaleUrgency.THREE_TO_SIX_MONTHS:
        return 'Dans 3 à 6 mois';
      case PropertySaleUrgency.MORE_THAN_SIX_MONTHS:
        return 'Dans plus de 6 mois';
      default:
        return value;
    }
  }

  static currentMarketingMode(value?: PropertyCurrentMarketingMode): string {
    if (!value) return '-';
    switch (value) {
      case PropertyCurrentMarketingMode.AGENCY_SIMPLE:
        return 'Mandat simple en agence';
      case PropertyCurrentMarketingMode.AGENCY_EXCLUSIVE:
        return 'Mandat exclusif en agence';
      case PropertyCurrentMarketingMode.PAP:
        return 'Entre particuliers (PAP)';
      case PropertyCurrentMarketingMode.AGENCY_AND_PAP:
        return 'Mandat simple en agence et PAP';
      default:
        return value;
    }
  }

  static characteristicCategory(
    categoryOrRef?: string | PropertyCharacteristicRefDto | null,
    characteristics?: PropertyCharacteristicsDto | null,
  ): string {
    if (!categoryOrRef) {
      return 'Pièce non spécifiée';
    }

    const categoryNames: Record<string, string> = {
      // General & Views
      general: 'Général',
      facade: 'Façade',
      view: 'Vue / Panorama',
      aerial_view: 'Vue aérienne / Drone',
      surroundings: 'Environnement / Quartier',
      exterior_general: 'Extérieur',
      floor_plan: 'Plan du bien',

      // General Sections
      interior: 'Intérieur',
      exterior: 'Extérieur',
      commonAreas: 'Espace commun',
      commonArea: 'Espace commun',
      parkings: 'Stationnement',
      parking: 'Stationnement',

      // Interior Spaces & Rooms
      rooms: 'Pièce',
      room: 'Pièce',
      bedroom: 'Chambre',
      bathroom: 'Salle de bain',
      showerRoom: "Salle d'eau",
      wc: 'WC',
      living_room: 'Salon / Séjour',
      dining_room: 'Salle à manger',
      office: 'Bureau',
      kitchen: 'Cuisine',
      library: 'Bibliothèque',
      workshop: 'Atelier',
      music_studio: 'Studio de musique',
      laundry_room: 'Buanderie',
      gym: 'Salle de sport',
      playroom: 'Salle de jeux',
      storage: 'Débarras',
      basement: 'Sous-sol',
      attic: 'Grenier',
      other: 'Autre',

      // Exterior
      balcony: 'Balcon',
      terrace: 'Terrasse',
      garden: 'Jardin',
      loggia: 'Loggia',
      swimmingPool: 'Piscine',
      pool: 'Piscine',
      cellar: 'Cave',
      courtyard: 'Cour',

      // Common areas
      entrance: 'Entrée',
      corridor: 'Couloir',
      hall: 'Hall',
      park: 'Parc',
      bike_room: 'Local vélo',
      stroller_room: 'Local poussette',
      trash_room: 'Local poubelle',
      meeting_room: 'Salle de réunion',

      // Parkings
      garage: 'Garage',
      interiorParking: 'Parking intérieur',
      exteriorParking: 'Parking extérieur',
      box: 'Box',
      carport: 'Carport',
      double_garage: 'Garage double',
      covered: 'Parking couvert',
      underground: 'Souterrain',
      outdoor: 'Place extérieure',
      street_parking: 'Stationnement en voirie',
    };

    // If a reference object is passed and full characteristics are available, resolve by exact space id
    if (typeof categoryOrRef === 'object' && categoryOrRef && characteristics) {
      const id = categoryOrRef.id;
      if (id) {
        // 1. Check interior rooms (kitchen, living_room, etc.)
        const room = characteristics.interior?.rooms?.find((r) => r.id === id);
        if (room) {
          const roomLabel = room.type ? EtxeEnumsNiceLabelUtils.room(room.type) : 'Pièce';
          return room.precision ? `${roomLabel} (${room.precision})` : roomLabel;
        }

        // 2. Check bedroom
        const bed = characteristics.interior?.bedroom?.find((b) => b.id === id);
        if (bed) {
          return bed.precision ? `Chambre (${bed.precision})` : 'Chambre';
        }

        // 3. Check bathroom
        const bath = characteristics.interior?.bathroom?.find((b) => b.id === id);
        if (bath) {
          return bath.precision ? `Salle de bain (${bath.precision})` : 'Salle de bain';
        }

        // 4. Check showerRoom
        const shower = characteristics.interior?.showerRoom?.find((s) => s.id === id);
        if (shower) {
          return shower.precision ? `Salle d'eau (${shower.precision})` : "Salle d'eau";
        }

        // 5. Check exterior spaces
        if (characteristics.exterior) {
          for (const [extKey, extList] of Object.entries(characteristics.exterior)) {
            if (Array.isArray(extList)) {
              const extItem = extList.find((item) => item.id === id);
              if (extItem) {
                const extLabel = categoryNames[extKey] || extKey;
                return extItem.precision ? `${extLabel} (${extItem.precision})` : extLabel;
              }
            }
          }
        }

        // 6. Check common areas
        const commonArea = characteristics.commonAreas?.find((c) => c.id === id);
        if (commonArea) {
          const caLabel = commonArea.type
            ? EtxeEnumsNiceLabelUtils.commonArea(commonArea.type)
            : 'Espace commun';
          return commonArea.precision ? `${caLabel} (${commonArea.precision})` : caLabel;
        }

        // 7. Check parkings
        const parking = characteristics.parkings?.find((p) => p.id === id);
        if (parking) {
          return parking.type ? EtxeEnumsNiceLabelUtils.parking(parking.type) : 'Parking';
        }
      }
    }

    let key: string;
    let section = '';

    if (typeof categoryOrRef === 'object') {
      key = categoryOrRef.category || '';
      section = categoryOrRef.section || '';
    } else {
      key = String(categoryOrRef);
    }

    if (key && categoryNames[key]) {
      return categoryNames[key];
    }
    if (section && categoryNames[section]) {
      return categoryNames[section];
    }
    if (key && key !== 'undefined' && key !== 'null') {
      return key;
    }
    return 'Pièce non spécifiée';
  }

  static verificationStatus(status?: PropertyVerificationStatus | null): string {
    switch (status) {
      case PropertyVerificationStatus.VERIFIED:
        return 'Validé';
      case PropertyVerificationStatus.REJECTED:
        return 'Refusé';
      case PropertyVerificationStatus.FLAGGED:
        return 'Signalé';
      case PropertyVerificationStatus.UNVERIFIED:
      default:
        return 'Non vérifié';
    }
  }

  static documentType(type?: PropertyDocumentType | null): string {
    switch (type) {
      case PropertyDocumentType.OWNERSHIP:
        return 'Titre / Taxe foncière';
      case PropertyDocumentType.DPE:
        return 'Diagnostic DPE / DDT';
      case PropertyDocumentType.CO_OWNERSHIP:
        return 'Copropriété / PV d’AG';
      case PropertyDocumentType.FLOOR_PLAN:
        return 'Plan du bien';
      case PropertyDocumentType.OTHER:
      default:
        return 'Autre document';
    }
  }

  static documentStatus(status?: PropertyDocumentStatus | null): string {
    switch (status) {
      case PropertyDocumentStatus.VERIFIED:
        return 'Validé';
      case PropertyDocumentStatus.PENDING_REVIEW:
        return 'En attente';
      case PropertyDocumentStatus.REJECTED:
        return 'Refusé';
      default:
        return 'En attente';
    }
  }

  static marketState(state?: PropertyMarketState | null): string {
    switch (state) {
      case PropertyMarketState.FOR_SALE:
        return 'À vendre';
      case PropertyMarketState.UNDER_MANDATE:
        return 'Sous mandat';
      case PropertyMarketState.UNDER_OFFER:
        return 'Offre acceptée';
      case PropertyMarketState.UNDER_CONTRACT:
        return 'Sous compromis';
      case PropertyMarketState.SOLD:
        return 'Vendu';
      case PropertyMarketState.WITHDRAWN:
        return 'Retiré';
      default:
        return state || '';
    }
  }

  static creationSource(source?: PropertyCreationSource | null): string {
    switch (source) {
      case PropertyCreationSource.OWNER:
        return 'Propriétaire';
      case PropertyCreationSource.AGENT:
        return 'Agent';
      case PropertyCreationSource.ADMIN:
        return 'Administrateur';
      default:
        return source || '';
    }
  }

  static mandateType(type?: PropertyMandateType | null): string {
    switch (type) {
      case PropertyMandateType.EXCLUSIVE:
        return 'Exclusif';
      case PropertyMandateType.SEMI_EXCLUSIVE:
        return 'Semi-exclusif';
      case PropertyMandateType.SIMPLE:
        return 'Simple';
      default:
        return type || 'Simple';
    }
  }

  static mandateDuration(duration?: PropertyMandateDuration | null): string {
    switch (duration) {
      case PropertyMandateDuration.TWO_MONTHS:
        return '2 mois';
      case PropertyMandateDuration.THREE_MONTHS:
        return '3 mois';
      case PropertyMandateDuration.SIX_MONTHS:
        return '6 mois';
      case PropertyMandateDuration.FLEXIBLE:
        return 'Flexible';
      default:
        return duration || '';
    }
  }

  static sellerPriority(priority?: PropertySellerPriority | null): string {
    switch (priority) {
      case PropertySellerPriority.SPEED:
        return 'Vente rapide';
      case PropertySellerPriority.MAX_PRICE:
        return 'Meilleur prix net vendeur';
      case PropertySellerPriority.PREMIUM_VISUALS:
        return 'Visuels premium (photos/vidéo)';
      case PropertySellerPriority.QUALIFIED_BUYERS:
        return 'Acquéreurs qualifiés & finançables';
      case PropertySellerPriority.VALUATION:
        return 'Estimation du bien';
      case PropertySellerPriority.FULL_DELEGATION:
        return 'Délégation totale';
      case PropertySellerPriority.DAILY_REPORTS:
        return 'Comptes-rendus réguliers';
      default:
        return priority || '';
    }
  }

  static keyManagement(type?: PropertyKeyManagement | null): string {
    switch (type) {
      case PropertyKeyManagement.AGENT_KEY_DOUBLE:
        return "Double des clés remis à l'agent";
      case PropertyKeyManagement.KEY_BOX:
        return 'Boîte à clés sécurisée à code';
      case PropertyKeyManagement.BY_APPOINTMENT_OWNER:
        return 'Sur rendez-vous en votre présence';
      default:
        return type || '';
    }
  }

  static creationStep(step?: string | null): string {
    if (!step) return 'Début';
    switch (step) {
      case 'definition':
        return 'Définition';
      case 'location':
        return 'Localisation';
      case 'key-infos':
        return 'Informations clés';
      case 'construction':
        return 'Construction';
      case 'characteristics':
        return 'Caractéristiques';
      case 'dpe':
        return 'DPE';
      case 'geohazards':
        return 'Géorisques';
      case 'presentation':
        return 'Présentation';
      case 'photos':
        return 'Photos';
      case 'financials':
        return 'Finances';
      case 'mandate':
        return 'Mandat';
      case 'ownership-document':
        return 'Justificatif de propriété';
      case 'completed':
        return 'Terminé';
      default:
        return step;
    }
  }

  static applicationService(service: PropertyApplicationService): string {
    switch (service) {
      case PropertyApplicationService.PROFESSIONAL_PHOTOS:
        return 'Photos professionnelles HDR';
      case PropertyApplicationService.VIRTUAL_TOUR_3D:
        return 'Visite virtuelle 3D immersive';
      case PropertyApplicationService.DRONE:
        return 'Prises de vue par drone';
      case PropertyApplicationService.HOME_STAGING:
        return 'Home staging virtuel';
      case PropertyApplicationService.PREMIUM_PORTALS:
        return 'Diffusion portails premium (SeLoger, LeBonCoin...)';
      case PropertyApplicationService.SOCIAL_MEDIA_ADS:
        return 'Campagnes ciblées réseaux sociaux';
      case PropertyApplicationService.FINANCIAL_SCREENING:
        return 'Filtrage bancaire strict des acquéreurs';
      case PropertyApplicationService.ACTIVE_BUYER_POOL:
        return "Portefeuille d'acquéreurs qualifiés sur le secteur";
      case PropertyApplicationService.REGULAR_REPORTING:
        return 'Comptes-rendus systématiques après visite';
      default:
        return service;
    }
  }

  static applicationServiceIcon(service: PropertyApplicationService): string {
    switch (service) {
      case PropertyApplicationService.PROFESSIONAL_PHOTOS:
        return 'photo_camera';
      case PropertyApplicationService.VIRTUAL_TOUR_3D:
        return 'view_in_ar';
      case PropertyApplicationService.DRONE:
        return 'flight';
      case PropertyApplicationService.HOME_STAGING:
        return 'chair';
      case PropertyApplicationService.PREMIUM_PORTALS:
        return 'travel_explore';
      case PropertyApplicationService.SOCIAL_MEDIA_ADS:
        return 'campaign';
      case PropertyApplicationService.FINANCIAL_SCREENING:
        return 'verified_user';
      case PropertyApplicationService.ACTIVE_BUYER_POOL:
        return 'groups';
      case PropertyApplicationService.REGULAR_REPORTING:
        return 'assessment';
      default:
        return 'check_circle';
    }
  }

  static applicationServiceDescription(service: PropertyApplicationService): string {
    switch (service) {
      case PropertyApplicationService.PROFESSIONAL_PHOTOS:
        return 'Mise en valeur optimale des volumes et de la luminosité.';
      case PropertyApplicationService.VIRTUAL_TOUR_3D:
        return 'Permet aux acquéreurs de se projeter avant la visite physique.';
      case PropertyApplicationService.DRONE:
        return "Vue d'ensemble du bien, de son terrain et de son environnement.";
      case PropertyApplicationService.HOME_STAGING:
        return 'Projection 3D des espaces encombrés ou à rénover.';
      case PropertyApplicationService.PREMIUM_PORTALS:
        return 'Positionnement en tête de liste sur les plus grands portails immobiliers.';
      case PropertyApplicationService.SOCIAL_MEDIA_ADS:
        return "Ciblage d'acheteurs passifs qualifiés sur Facebook et Instagram.";
      case PropertyApplicationService.FINANCIAL_SCREENING:
        return "Vérification de la capacité d'emprunt et accord bancaire préalable.";
      case PropertyApplicationService.ACTIVE_BUYER_POOL:
        return 'Matching immédiat avec les recherches actives enregistrées en agence.';
      case PropertyApplicationService.REGULAR_REPORTING:
        return "Bilan détaillé après chaque visite et point d'étape hebdomadaire.";
      default:
        return '';
    }
  }

  static applicationStatus(status: PropertyApplicationStatus): string {
    switch (status) {
      case PropertyApplicationStatus.PENDING:
        return 'Candidature en attente';
      case PropertyApplicationStatus.VISIT_INVITED:
        return 'Invitation à visiter';
      case PropertyApplicationStatus.VISIT_CONFIRMED:
        return 'En attente de visite';
      case PropertyApplicationStatus.VISITED:
        return 'Bien visité';
      case PropertyApplicationStatus.OFFER_SUBMITTED:
        return 'Offre complète transmise';
      case PropertyApplicationStatus.ACCEPTED:
        return 'Mandat attribué';
      case PropertyApplicationStatus.REJECTED:
        return 'Candidature non retenue';
      case PropertyApplicationStatus.CANCELLED:
        return 'Candidature annulée';
      default:
        return status;
    }
  }

  static noiseSource(sourceOrSources?: string | string[] | null): string {
    if (!sourceOrSources) return '';
    const list = Array.isArray(sourceOrSources) ? sourceOrSources : [sourceOrSources];
    return list
      .map((s) => {
        const key = s.toLowerCase().trim();
        switch (key) {
          case 'rail_transit':
            return 'Lignes ferroviaires et gares';
          case 'bus_transit':
            return 'Transports en commun (bus)';
          case 'commercial_activity':
            return 'Commerces et activités de proximité';
          case 'industrial_activity':
            return 'Activités industrielles (ICPE)';
          case 'road_traffic':
            return 'Trafic routier et axes de circulation';
          case 'air_traffic':
            return 'Couloir aérien et aérodrome';
          case 'urban_activity':
            return 'Activités urbaines animées';
          case 'highway':
            return 'Autoroute ou voie rapide';
          case 'rail':
            return 'Lignes ferroviaires';
          case 'shops':
            return 'Commerces et services';
          case 'industry':
            return 'Installations industrielles';
          default: {
            const cleaned = key.replace(/_/g, ' ');
            return cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
          }
        }
      })
      .join(', ');
  }
}
